import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'

const TEMPLATE_VARIABLES = [
  { label: '{caller_name}', value: '{caller_name}', example: 'Jane Doe' },
  { label: '{campaign_name}', value: '{campaign_name}', example: 'Lamano Law' },
  { label: '{disposition}', value: '{disposition}', example: 'Hot Lead - No Appointment' },
  { label: '{agent_name}', value: '{agent_name}', example: 'John Smith' },
  { label: '{call_date}', value: '{call_date}', example: '3/12/2026' }
]

const SMS_SEGMENT_SIZE = 160

const MAX_VISIBLE_ITEMS = 50

/**
 * Reusable collapsible checkbox list with search and select-all.
 * Memoized — only re-renders when its own props change, not when
 * sibling state (e.g. template text) updates.
 */
const CheckboxListSection = React.memo(({
  title,
  items,
  selectedSet,
  selectedCount,
  onToggle,
  onToggleAll,
  emptyHint,
  searchPlaceholder,
  defaultExpanded
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search) return items
    const lower = search.toLowerCase()
    return items.filter(item => item.toLowerCase().includes(lower))
  }, [items, search])

  const visible = filtered.length > MAX_VISIBLE_ITEMS ? filtered.slice(0, MAX_VISIBLE_ITEMS) : filtered
  const allSelected = selectedCount === items.length && items.length > 0
  const someSelected = selectedCount > 0 && selectedCount < items.length

  return (
    <Box mt={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2">
          {title}
          {selectedCount > 0 && (
            <Chip
              label={`${selectedCount} selected`}
              size="small"
              color="primary"
              style={{ marginLeft: 8 }}
            />
          )}
          {selectedCount === 0 && emptyHint && (
            <Typography variant="caption" color="textSecondary" component="span" style={{ marginLeft: 8 }}>
              ({emptyHint})
            </Typography>
          )}
        </Typography>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
      </Box>

      {expanded && (
        <Box mt={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">Select All</Typography>}
            />
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder || 'Search...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <FontAwesomeIcon icon={faSearch} style={{ marginRight: 8, color: '#999' }} />
            }}
          />
          <Divider style={{ margin: '8px 0' }} />
          <List dense style={{ maxHeight: 200, overflow: 'auto' }}>
            {visible.map((item) => (
              <ListItem
                key={item}
                dense
                button
                onClick={() => onToggle(item)}
              >
                <Checkbox
                  edge="start"
                  checked={selectedSet.has(item)}
                  color="primary"
                  size="small"
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
            {filtered.length > MAX_VISIBLE_ITEMS && (
              <Box textAlign="center" py={1}>
                <Typography variant="caption" color="textSecondary">
                  Showing {MAX_VISIBLE_ITEMS} of {filtered.length} — use search to narrow
                </Typography>
              </Box>
            )}
            {filtered.length === 0 && (
              <Box textAlign="center" p={2}>
                <Typography variant="body2" color="textSecondary">
                  {search ? 'No results match your search.' : 'No items available.'}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      )}
    </Box>
  )
})

CheckboxListSection.displayName = 'CheckboxListSection'

CheckboxListSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSet: PropTypes.instanceOf(Set).isRequired,
  selectedCount: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  emptyHint: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  defaultExpanded: PropTypes.bool
}

const SmsAutomationDialog = ({
  open,
  onClose,
  automation = null,
  dispositions,
  campaigns,
  onSave,
  isEditing = false
}) => {
  const [name, setName] = useState('')
  const [selectedDispositions, setSelectedDispositions] = useState([])
  const [template, setTemplate] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedCampaigns, setSelectedCampaigns] = useState({})

  useEffect(() => {
    if (automation && open) {
      setName(automation.name || '')
      const configDispositions = automation.config?.trigger?.dispositions
      const legacyDisposition = automation.config?.trigger?.disposition
      if (configDispositions && Array.isArray(configDispositions)) {
        setSelectedDispositions(configDispositions)
      } else if (legacyDisposition) {
        setSelectedDispositions([legacyDisposition])
      } else {
        setSelectedDispositions([])
      }
      setTemplate(automation.config?.sms?.template || '')
      setEnabled(automation.enabled !== false)
      setSelectedCampaigns(automation.campaign_subscriptions || {})
    }
  }, [automation, open])

  const handleReset = () => {
    setName('')
    setSelectedDispositions([])
    setTemplate('')
    setEnabled(true)
    setSelectedCampaigns({})
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const automationData = {
        ...(isEditing && automation ? { id: automation.id } : {}),
        name,
        type: 'sms_on_disposition',
        enabled,
        config: {
          trigger: { dispositions: selectedDispositions },
          sms: { template }
        },
        campaign_subscriptions: selectedCampaigns
      }
      const result = await onSave(automationData)
      if (result !== false) {
        handleReset()
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Disposition toggle handlers (stable refs for memoized child)
  const handleToggleDisposition = useCallback((disposition) => {
    setSelectedDispositions(prev =>
      prev.includes(disposition)
        ? prev.filter(d => d !== disposition)
        : [...prev, disposition]
    )
  }, [])

  const handleToggleAllDispositions = useCallback((enable) => {
    setSelectedDispositions(enable ? [...dispositions] : [])
  }, [dispositions])

  // Campaign toggle handlers (stable refs for memoized child)
  const handleToggleCampaign = useCallback((campaignName) => {
    setSelectedCampaigns(prev => {
      const next = { ...prev }
      if (next[campaignName]) {
        delete next[campaignName]
      } else {
        next[campaignName] = { email_sent_this_month: false, last_sent: null }
      }
      return next
    })
  }, [])

  const handleToggleAllCampaigns = useCallback((enable) => {
    if (enable) {
      const all = {}
      campaigns.forEach(c => {
        all[c.campaign_name] = { email_sent_this_month: false, last_sent: null }
      })
      setSelectedCampaigns(all)
    } else {
      setSelectedCampaigns({})
    }
  }, [campaigns])

  const insertVariable = (variable) => {
    setTemplate(prev => prev + variable)
  }

  const getPreviewText = () => {
    let preview = template
    TEMPLATE_VARIABLES.forEach(v => {
      preview = preview.replace(new RegExp(v.value.replace(/[{}]/g, '\\$&'), 'g'), v.example)
    })
    return preview
  }

  const campaignNames = useMemo(() => campaigns.map(c => c.campaign_name), [campaigns])
  const selectedDispositionSet = useMemo(() => new Set(selectedDispositions), [selectedDispositions])
  const selectedCampaignNames = useMemo(() => Object.keys(selectedCampaigns), [selectedCampaigns])
  const selectedCampaignSet = useMemo(() => new Set(selectedCampaignNames), [selectedCampaignNames])
  const charCount = template.length
  const segmentCount = Math.ceil(charCount / SMS_SEGMENT_SIZE) || 1
  const isValid = name.trim() && selectedDispositions.length > 0 && template.trim()

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit SMS Automation' : 'Create SMS Automation'}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Automation Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          placeholder="e.g., Hot Lead Follow-up SMS"
        />

        {/* Trigger Dispositions */}
        <CheckboxListSection
          title="Trigger Dispositions"
          items={dispositions}
          selectedSet={selectedDispositionSet}
          selectedCount={selectedDispositions.length}
          onToggle={handleToggleDisposition}
          onToggleAll={handleToggleAllDispositions}
          searchPlaceholder="Search dispositions..."
          emptyHint="at least one required"
          defaultExpanded
        />

        <Box mt={2} mb={1}>
          <Typography variant="subtitle2" gutterBottom>
            SMS Template
          </Typography>
          <Box mb={1} display="flex" flexWrap="wrap" style={{ gap: 4 }}>
            {TEMPLATE_VARIABLES.map(v => (
              <Chip
                key={v.value}
                label={v.label}
                size="small"
                clickable
                color="primary"
                variant="outlined"
                onClick={() => insertVariable(v.value)}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Hi {caller_name}, thank you for calling {campaign_name}..."
          />
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="textSecondary">
              {charCount} characters ({segmentCount} SMS segment{segmentCount !== 1 ? 's' : ''})
            </Typography>
            {charCount > SMS_SEGMENT_SIZE && (
              <Typography variant="caption" color="error">
                Messages over {SMS_SEGMENT_SIZE} chars are sent as multiple segments
              </Typography>
            )}
          </Box>
        </Box>

        {template && (
          <Box mt={2} p={2} borderRadius={4} style={{ backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-primary)' }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
              {getPreviewText()}
            </Typography>
          </Box>
        )}

        {/* Campaign Selection */}
        <CheckboxListSection
          title="Campaigns"
          items={campaignNames}
          selectedSet={selectedCampaignSet}
          selectedCount={selectedCampaignNames.length}
          onToggle={handleToggleCampaign}
          onToggleAll={handleToggleAllCampaigns}
          searchPlaceholder="Search campaigns..."
          emptyHint="all campaigns if none selected"
        />

        <Box mt={2}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Enabled"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!isValid || isSaving}
        >
          {isEditing ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

SmsAutomationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automation: PropTypes.object,
  dispositions: PropTypes.array.isRequired,
  campaigns: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  isEditing: PropTypes.bool
}

export default SmsAutomationDialog
